import React, { useEffect, useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    StatusBar,
    Platform,
    Image,
    RefreshControl,
    ScrollView,
    Linking,
    Alert,
} from 'react-native';
import TabBar from '../components/TabBar';
import { SkeletonReportItem } from '../components/Skeleton';
import { v2GetMyDocuments, v2GetDocumentDownloadUrl, v2MarkDocumentsViewed } from '../services/v2/api';
import type { V2Document } from '../types/v2';
import { useAuth } from '../contexts/AuthContext';
import { openWindowInTap } from '../../web/openLater';
import { COLORS } from '../theme/themeTokens';

// Helper to check if document is PDF
const isPdfDocument = (contentType: string): boolean => {
    return contentType?.toLowerCase() === 'application/pdf';
};

// Helper to format date
const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};

// Helper to format filename for display
const formatReportName = (filename: string, reportId: string): string => {
    // If filename exists, use it; otherwise use report_id
    if (filename) {
        // Remove extension for cleaner display
        return filename.replace(/\.[^/.]+$/, '');
    }
    return `Report_#_${reportId}`;
};

// Report Item Component
const ReportItem = ({
    name,
    date,
    onPress,
    onDownload,
    isDownloading,
}: {
    name: string;
    date: string;
    onPress: () => void;
    onDownload: () => void;
    isDownloading: boolean;
}) => (
    <TouchableOpacity style={styles.reportItem} onPress={onPress} activeOpacity={0.7}>
        <Image
            source={require('../assets/doc.png')}
            style={styles.reportIcon}
            resizeMode="contain"
        />
        <View style={styles.reportInfo}>
            <Text style={styles.reportName} numberOfLines={1}>
                {name}
            </Text>
            <Text style={styles.reportDate}>{date}</Text>
        </View>
        <TouchableOpacity
            onPress={(e) => {
                e.stopPropagation();
                onDownload();
            }}
            style={styles.downloadButton}
            disabled={isDownloading}>
            <Image
                source={require('../assets/upload.png')}
                style={[styles.downloadIcon, isDownloading && styles.downloadingIcon]}
                resizeMode="contain"
            />
        </TouchableOpacity>
    </TouchableOpacity>
);

type TabScreen = 'dashboard' | 'products' | 'history' | 'reports' | 'support' | 'profile';

interface ReportsScreenProps {
    onNavigate: (screen: TabScreen) => void;
    onOpenPdfPreview?: (document: V2Document) => void;
    onBack?: () => void;
}

const ReportsScreen: React.FC<ReportsScreenProps> = ({ onNavigate, onOpenPdfPreview, onBack }) => {
    const statusBarHeight = Platform.OS === 'android' ? 28 : 20;
    const [documents, setDocuments] = useState<V2Document[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const { handleSessionExpired, user } = useAuth();

    const fetchDocuments = useCallback(async () => {
        try {
            setError(null);
            const response = await v2GetMyDocuments();
            setDocuments(response.documents || []);
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : 'Failed to load reports';
            console.error('Failed to fetch documents:', err);

            if (
                errorMessage.includes('Session expired') ||
                errorMessage.includes('Not authenticated')
            ) {
                handleSessionExpired(errorMessage);
                return;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [handleSessionExpired]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    // Mark documents as viewed when the screen is opened. The app-icon badge
    // counts chat only, so an unopened report never sets it and there is
    // nothing to refresh here; the flag still tells OneAdmin the client
    // opened the report.
    // Skipped under impersonation — the target user would otherwise see
    // "viewed" state from a session they didn't initiate. Backend also
    // hard-denies POST /documents/mark-viewed, so this is double-defense.
    useEffect(() => {
        const canMark = user?.capabilities?.can_mark_docs_viewed ?? true;
        if (!canMark) {
            return;
        }
        const markDocumentsViewed = async () => {
            try {
                await v2MarkDocumentsViewed();
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to mark as viewed';
                console.error('Failed to mark documents as viewed:', err);

                if (errorMessage.includes('Session expired') || errorMessage.includes('Not authenticated')) {
                    handleSessionExpired(errorMessage);
                }
            }
        };

        markDocumentsViewed();
    }, [handleSessionExpired, user?.capabilities?.can_mark_docs_viewed]);

    const handleRefresh = useCallback(() => {
        setRefreshing(true);
        fetchDocuments();
    }, [fetchDocuments]);

    // Handle card press - PDF preview for PDFs, browser for others
    // Web: every report opens in the browser's own viewer (all pages, save,
    // share) — the in-app preview shows only the first page of a PDF there.
    const handleCardPress = useCallback(async (document: V2Document) => {
        if (Platform.OS !== 'web' && isPdfDocument(document.content_type) && onOpenPdfPreview) {
            // Open PDF preview in-app
            onOpenPdfPreview(document);
        } else {
            // Open in browser for non-PDF files
            const pending = openWindowInTap();
            try {
                setDownloadingId(document.id);
                const response = await v2GetDocumentDownloadUrl(document.id);
                if (response.download_url) {
                    await (pending ? pending.open(response.download_url) : Linking.openURL(response.download_url));
                } else {
                    pending?.cancel();
                }
            } catch (err) {
                pending?.cancel();
                const errorMessage = err instanceof Error ? err.message : 'Failed to open document';

                if (errorMessage.includes('Session expired') || errorMessage.includes('Not authenticated')) {
                    handleSessionExpired(errorMessage);
                    return;
                }

                Alert.alert('Error', 'Failed to open document');
            } finally {
                setDownloadingId(null);
            }
        }
    }, [onOpenPdfPreview, handleSessionExpired]);

    // Handle download button - always open in browser
    const handleDownload = useCallback(async (documentId: string) => {
        const pending = openWindowInTap();
        try {
            setDownloadingId(documentId);
            const response = await v2GetDocumentDownloadUrl(documentId);
            if (response.download_url) {
                await (pending ? pending.open(response.download_url) : Linking.openURL(response.download_url));
            } else {
                pending?.cancel();
                Alert.alert('Error', 'No download URL received');
            }
        } catch (err) {
            pending?.cancel();
            const errorMessage = err instanceof Error ? err.message : 'Failed to download document';

            if (errorMessage.includes('Session expired') || errorMessage.includes('Not authenticated')) {
                handleSessionExpired(errorMessage);
                return;
            }

            Alert.alert('Error', 'Failed to download document');
        } finally {
            setDownloadingId(null);
        }
    }, [handleSessionExpired]);

    return (
        <View style={styles.container}>
            <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

            {/* Header with back button. Whole row is tappable — same
                pattern as `SimpleHeader` on neighbouring screens, so a
                tap anywhere on the navy strip (icon, title text, the
                empty right-side gap) triggers Back. Previously only the
                tiny chevron was clickable; users hitting the "Reports"
                label thought nothing was happening. */}
            <View style={[styles.headerBackground, { paddingTop: statusBarHeight }]}>
                <TouchableOpacity
                    style={styles.headerRow}
                    activeOpacity={0.7}
                    onPress={() => (onBack ? onBack() : onNavigate('products'))}
                >
                    <View style={styles.backButton}>
                        <Image
                            source={require('../assets/arrow_left_20_white.png')}
                            style={styles.backIcon}
                            resizeMode="contain"
                        />
                    </View>
                    <Text style={styles.headerTitle}>Reports</Text>
                    <View style={styles.backButton} />
                </TouchableOpacity>
            </View>

            {/* Main Content */}
            <ScrollView
                style={styles.content}
                contentContainerStyle={styles.contentContainer}
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={handleRefresh}
                        tintColor={COLORS.brandOrange}
                    />
                }>

                {loading ? (
                    <View style={styles.reportsList}>
                        <SkeletonReportItem />
                        <View style={styles.separator} />
                        <SkeletonReportItem />
                        <View style={styles.separator} />
                        <SkeletonReportItem />
                    </View>
                ) : error ? (
                    <View style={styles.errorContainer}>
                        <Text style={styles.errorText}>{error}</Text>
                        <TouchableOpacity style={styles.retryButton} onPress={fetchDocuments}>
                            <Text style={styles.retryText}>Retry</Text>
                        </TouchableOpacity>
                    </View>
                ) : documents.length === 0 ? (
                    <View style={styles.emptyContainer}>
                        <Text style={styles.emptyText}>No reports available</Text>
                    </View>
                ) : (
                    <View style={styles.reportsList}>
                        {documents.map((doc, index) => (
                            <React.Fragment key={doc.id}>
                                <ReportItem
                                    name={formatReportName(doc.filename, doc.report_id)}
                                    date={formatDate(doc.created_at)}
                                    onPress={() => handleCardPress(doc)}
                                    onDownload={() => handleDownload(doc.id)}
                                    isDownloading={downloadingId === doc.id}
                                />
                                {index < documents.length - 1 && (
                                    <View style={styles.separator} />
                                )}
                            </React.Fragment>
                        ))}
                    </View>
                )}

                {/* Spacer for TabBar */}
                <View style={{ height: 120 }} />
            </ScrollView>

            {/* Tab Bar */}
            <TabBar activeTab="reports" onNavigate={onNavigate} />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.primaryNavy,
    },
    headerBackground: {
        backgroundColor: COLORS.primaryNavy,
        paddingBottom: 20,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        height: 44,
    },
    backButton: {
        width: 40,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    backIcon: {
        width: 20,
        height: 20,
        tintColor: COLORS.white,
    },
    headerTitle: {
        color: COLORS.white,
        fontSize: 20,
        fontWeight: '600',
        textAlign: 'center',
    },
    content: {
        flex: 1,
        backgroundColor: COLORS.pureLight,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        marginTop: -10,
    },
    contentContainer: {
        paddingHorizontal: 20,
        paddingTop: 8,
    },
    title: {
        fontSize: 26,
        fontWeight: '700',
        color: COLORS.deepNavy,
        marginBottom: 24,
    },
    reportsList: {
        paddingTop: 0,
    },
    reportItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
    },
    reportIcon: {
        width: 22,
        height: 22,
        marginRight: 12,
    },
    reportInfo: {
        flex: 1,
        gap: 7,
    },
    reportName: {
        fontFamily: 'Inter',
        fontSize: 14,
        fontWeight: '400',
        fontStyle: 'normal',
        lineHeight: 20,
        color: COLORS.primaryNavy,
    },
    reportDate: {
        fontFamily: 'Inter',
        fontSize: 12,
        fontWeight: '500',
        fontStyle: 'normal',
        lineHeight: 14,
        color: COLORS.blueSteel,
    },
    downloadButton: {
        padding: 8,
    },
    downloadIcon: {
        width: 24,
        height: 24,
    },
    downloadingIcon: {
        opacity: 0.5,
    },
    separator: {
        height: 1,
        backgroundColor: COLORS.softGrey,
        marginVertical: 4,
    },
    errorContainer: {
        padding: 20,
        alignItems: 'center',
        gap: 12,
    },
    errorText: {
        fontSize: 16,
        color: COLORS.brandOrange,
        textAlign: 'center',
    },
    retryButton: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        backgroundColor: COLORS.brandOrange,
        borderRadius: 8,
    },
    retryText: {
        fontSize: 16,
        color: COLORS.white,
        fontWeight: '500',
    },
    emptyContainer: {
        padding: 40,
        alignItems: 'center',
    },
    emptyText: {
        fontSize: 18,
        color: COLORS.blueSteel,
        textAlign: 'center',
    },
});

export default ReportsScreen;
